/**
 * Shared snapshot count helper — every code path that produces a
 * BoxesSnapshot goes through here so the Map chips, BoxesPage chips,
 * and Dashboard donut always agree.
 *
 * Bucketing rule (matches DashboardPage.tsx — Push K Slice 5 / commit
 * 95c4218): a stacked box is "intentionally offline" and gets pulled
 * OUT of its underlying state into the synthetic `stacked_count`
 * sibling. That way an UP-and-stacked box doesn't inflate the UP
 * slice; operators can still find it via the dedicated STACKED chip.
 *
 *   total_boxes = boxes.length
 *   stacked_count = boxes filter is_stacked
 *   state_counts[state] = boxes filter NOT is_stacked && b.state === state
 */

import type { Box, BoxesSnapshot, State } from "../types/boxes";

export const ALL_STATES: State[] = [
  "UP",
  "SCANNER_ONLY",
  "CAMERA_ONLY",
  "DOWN",
  "POWERED_DOWN",
  "UNTOUCHED",
  "UNKNOWN",
];

/** A box's display bucket. Stacked is a bucket of its own, NOT a state:
 *  the box is intentionally offline, so its underlying state (usually DOWN,
 *  because nothing is reporting) is not something to triage. */
export type BoxBucket = State | "STACKED";

/**
 * THE one place the bucketing rule lives.
 *
 * It used to live only inside `computeStateCounts`, which meant every chip
 * count was right and anything that FILTERED had to re-implement it. BoxesPage
 * filtered on `b.state` directly, so clicking "Down" listed stacked boxes that
 * the Down chip had deliberately excluded — the count and the list disagreed
 * with each other (reported on SB078, stacked at WT Yard 7 and classified
 * DOWN). Anything that buckets, counts or filters calls this.
 */
export function bucketOf(b: Box): BoxBucket {
  return b.is_stacked ? "STACKED" : b.state;
}

export interface ComputedCounts {
  total_boxes: number;
  state_counts: Record<State, number>;
  stacked_count: number;
}

export function computeStateCounts(boxes: Box[]): ComputedCounts {
  const counts = Object.fromEntries(
    ALL_STATES.map((s) => [s, 0]),
  ) as Record<State, number>;
  let stacked = 0;
  for (const b of boxes) {
    const bucket = bucketOf(b);
    if (bucket === "STACKED") {
      stacked += 1;
      continue;
    }
    counts[bucket] = (counts[bucket] ?? 0) + 1;
  }
  return {
    total_boxes: boxes.length,
    state_counts: counts,
    stacked_count: stacked,
  };
}

/** Re-derive totals on top of an existing snapshot — the writer's
 *  pre-computed totals (in static boxes.json) still count stacked
 *  inside their underlying state, so we overwrite them on read.
 *  Use this instead of trusting `snap.state_counts` directly. */
export function withRecomputedCounts(
  snap: BoxesSnapshot | Omit<BoxesSnapshot, "total_boxes" | "state_counts" | "stacked_count">,
): BoxesSnapshot {
  return { ...snap, ...computeStateCounts(snap.boxes) };
}
